
<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCertificatesStore } from '@/stores/certificates'
import UTACertificatesCreate from '@/components/UTACertificatesCreate.vue'
import Modal from '@/components/UTAModal.vue'
import buildFile from '@/helpers/buildFile'

const props = defineProps({
    imei: {
        type: String,
        required: false
    }
})

const translaterStore = useTranslaterStore()
const certificatesStore = useCertificatesStore()

const showReissueCertificate = ref(false) 

const getTranslate                  = computed(() => translaterStore.getTranslate)
const certificate                   = computed(() => certificatesStore.certificate)
const certificateProcess            = computed(() => certificatesStore.certificateProcess)
const certificateMsg                = computed(() => certificatesStore.certificateMsg)
const certificatesUpdateProcess     = computed(() => certificatesStore.certificatesUpdateProcess)
const certificatesUpdateMsg         = computed(() => certificatesStore.certificatesUpdateMsg)

function buildCerificateDate(tmString) {

    return new Date(Number(tmString) * 1000).toLocaleString()
}

certificatesStore.readCertificate({ uuid_imei_array: [props.imei] })

watch(certificatesUpdateMsg, msg => {
    if('success' in msg && msg.success) {
        showReissueCertificate.value = false
    }
})

</script>

<template>

    <div class="my-4 adt-certificate">

        <div class="container-fluid">

            <header class="page__header uta-header" role="heading" aria-level="1">
                <h1 class="uta-header__title">{{ props.imei }}</h1>
                <div class="spinner-border uta-header__spinner" role="status" v-if="certificateProcess || certificatesUpdateProcess">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <a 
                        href="#"
                        class="uta-header__action uta-btn"
                        @click.prevent="showReissueCertificate = true"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__text">{{ getTranslate.BTN_REISSUE }}</span>
                        </span>
                    </a>
                    <a 
                        href="#"
                        class="uta-header__action uta-btn uta-btn--main"
                        @click.prevent="buildFile(certificate.crt, 'application/x-x509-ca-cert', 'client_crt.crt')"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__text">{{ getTranslate.CERTIFICATES_DOWNLOAD_CRT }}</span>
                        </span>
                    </a>
                    <a 
                        href="#"
                        class="uta-header__action uta-btn uta-btn--main"
                        @click.prevent="buildFile(certificate.key, 'application/x-pem-file', 'client_key.key')"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__text">{{ getTranslate.CERTIFICATES_DOWNLOAD_KEY }}</span>
                        </span>
                    </a>
                </div>
            </header>

            <div 
                v-if="'success' in certificateMsg && !certificateMsg.success"
                class="uta-alert uta-alert--danger"
            >
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate[certificateMsg.text] || certificateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesMsg({})"></button>
            </div>

            <div 
                v-if="'success' in certificatesUpdateMsg && certificatesUpdateMsg.success"
                class="uta-alert uta-alert--success"
            >
                <span class="material-icons uta-alert__icon">
                    error_outline
                </span>
                <div class="uta-alert__text">
                    {{ getTranslate[certificatesUpdateMsg.text] || certificatesUpdateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="certificatesStore.setCertificatesUpdateMsg({})"></button>
            </div>

            <div class="uta-list">

                <div class="row uta-list__item">
                    <div class="col-3"><strong>{{ getTranslate.CERTIFICATES_DATE_START }}</strong>: </div>
                    <div class="col-9">{{ buildCerificateDate(certificate.start) }}</div>
                </div>

                <div class="row uta-list__item">
                    <div class="col-3"><strong>{{ getTranslate.CERTIFICATES_DATE_STOP }}</strong>: </div>
                    <div class="col-9">{{ buildCerificateDate(certificate.stop) }}</div>
                </div>

                <div class="row uta-list__item">
                    <div class="col-3"><strong>{{ getTranslate.CERTIFICATES_CRT }}</strong>: </div>
                    <div class="col-9">
                        <pre>{{ certificate.crt }}</pre>
                    </div>
                </div>

                <div class="row uta-list__item">
                    <div class="col-3"><strong>{{ getTranslate.CERTIFICATES_KEY }}</strong>: </div>
                    <div class="col-9">
                        <pre>{{ certificate.key }}</pre>
                    </div>
                </div>

                <div class="row uta-list__item">
                    <div class="col-3"><strong>{{ getTranslate.CERTIFICATES_INFO }}</strong>: </div>
                    <div class="col-9"><pre>{{ certificate.text }}</pre></div>
                </div>

            </div>

            <transition name="bounce">
                <Modal 
                    v-if="showReissueCertificate"
                    :header="getTranslate.CERTIFICATES_UPDATE"
                    size="lg"
                    @closeModal="showReissueCertificate = false"
                >
                    <template v-slot:body>
                        <UTACertificatesCreate 
                            :certificate="certificate"
                            :imei="props.imei"
                            @closeModal="showReissueCertificate = false" 
                        />
                    </template>
                </Modal>
            </transition>

        </div>

    </div>

</template>
